import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
  type Canceler,
} from 'axios'
import { getCache, setCache, removeRequest } from './cache'
import authService from './authService'

type HttpMethod =
  | 'request'
  | 'head'
  | 'options'
  | 'get'
  | 'post'
  | 'put'
  | 'patch'
  | 'delete'

interface RequestKeyConfig {
  method?: string
  url?: string
  data?: unknown
}

const CancelToken = axios.CancelToken

const PENDING_REQUESTS: Record<string, Canceler> = {}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
})

const METHODS = {
  request: axiosInstance.request,
  head: axiosInstance.head,
  options: axiosInstance.options,
  get: axiosInstance.get,
  post: axiosInstance.post,
  put: axiosInstance.put,
  patch: axiosInstance.patch,
  delete: axiosInstance.delete,
}

function getRequestId(method: string | undefined, config: unknown[]): string {
  const url = config[0]
  let body

  if (method === 'post' || method === 'put' || method === 'patch') {
    body = config[1]

    if (typeof body !== 'string') {
      body = JSON.stringify(body)
    }
  }

  return `${method} ${url} ${body || ''}`.trim()
}

function getKeyRequest(config: RequestKeyConfig): string {
  return getRequestId(config.method, [config.url, config.data])
}

function handlePendingRequest(
  config: RequestKeyConfig,
  cancelFn: Canceler,
): void {
  const key = getKeyRequest(config)

  PENDING_REQUESTS[key] = cancelFn
}

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.cancelToken = new CancelToken((cancelFn) => {
    handlePendingRequest(config, cancelFn)
  })
  return config
})

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const key = getKeyRequest(response.config)
    removeRequest(key)
    return response
  },
  (error: AxiosError) => {
    const requestConfig = error.response ? error.response.config : error.config
    const key = getKeyRequest(requestConfig ?? {})
    removeRequest(key)

    const statusCode = error.response ? error.response.status : 0
    if (statusCode === 401) {
      authService.logout(true)
    }

    return Promise.reject(error)
  },
)

function handlerCache(method: HttpMethod, params: unknown[]): Promise<unknown> {
  const key = getRequestId(method, params)
  const cached = getCache(key)
  if (cached) {
    return cached
  }
  const fn = METHODS[method] as (...args: unknown[]) => Promise<unknown>
  const promise = fn(...params)

  setCache(key, promise)
  return promise
}

type CachedApi = AxiosInstance & {
  [K in HttpMethod]: (...args: unknown[]) => Promise<AxiosResponse>
}

export default Object.keys(METHODS).reduce(
  (acum, current) => {
    acum[current] = (...args: unknown[]) =>
      handlerCache(current as HttpMethod, args)
    return acum
  },
  {
    ...axiosInstance,
  } as Record<string, unknown>,
) as unknown as CachedApi
